import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from 'react';

/* ──────────────────────────── helpers ──────────────────────────── */
const uid = () => Math.random().toString(36).slice(2, 10);

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

/* ──────────────────────────── data model ──────────────────────── */
const createCell = (overrides = {}) => ({
  id: uid(),
  content: '',
  component: null,       // null | { type, props }
  colspan: 1,
  rowspan: 1,
  width: null,           // px or null (auto)
  height: null,
  bgColor: '#ffffff',
  borderColor: null,     // null = inherit from table
  borderWidth: null,     // null = inherit from table
  borderStyle: null,     // null = inherit from table
  padding: 8,
  align: 'left',
  valign: 'top',
  isTitle: false,
  ...overrides,
});

const createTableModel = (rows = 2, cols = 2) => {
  const grid = [];
  const colWidths = Array(cols).fill(100 / cols);
  for (let r = 0; r < rows; r++) {
    const row = [];
    for (let c = 0; c < cols; c++) {
      row.push(createCell());
    }
    grid.push(row);
  }
  return {
    id: uid(),
    grid,
    colWidths,       // percentages
    rowHeights: Array(rows).fill(null), // null = auto
    borderCollapse: true,
    tableBorderColor: '#000000',
    tableBorderWidth: 1,
    tableBorderStyle: 'solid',  // 'solid' | 'dashed' | 'dotted' | 'none'
    tableWidth: 100,  // percent
    tableLoopConditions: [],  // [{ id, type: 'if'|'range', value: '' }]
  };
};

/* ──────── grid mutation helpers (immutable) ──────── */
const cloneModel = (m) => JSON.parse(JSON.stringify(m));

const addRow = (model, afterIdx) => {
  const m = cloneModel(model);
  const cols = m.grid[0]?.length || 2;
  const row = Array.from({ length: cols }, () => createCell());
  const idx = afterIdx != null ? afterIdx + 1 : m.grid.length;
  m.grid.splice(idx, 0, row);
  // Keep existing rows at same pixel size by distributing percentages
  const oldCount = m.rowHeights.length;
  const newRowPct = 100 / (oldCount + 1);
  const scale = (100 - newRowPct) / 100;
  for (let i = 0; i < m.rowHeights.length; i++) {
    m.rowHeights[i] = m.rowHeights[i] != null ? m.rowHeights[i] * scale : null;
  }
  m.rowHeights.splice(idx, 0, newRowPct);
  return m;
};

const addCol = (model, afterIdx) => {
  const m = cloneModel(model);
  const oldCount = m.colWidths.length;
  const idx = afterIdx != null ? afterIdx + 1 : (m.grid[0]?.length || 0);
  m.grid.forEach((row) => row.splice(idx, 0, createCell()));
  // Keep existing columns at same pixel size by scaling percentages
  // New column gets a share equal to average existing column
  const newColPct = 100 / (oldCount + 1);
  const scale = (100 - newColPct) / 100;
  for (let i = 0; i < m.colWidths.length; i++) m.colWidths[i] *= scale;
  m.colWidths.splice(idx, 0, newColPct);
  return m;
};

const removeRow = (model, idx) => {
  if (model.grid.length <= 1) return model;
  const m = cloneModel(model);
  m.grid.splice(idx, 1);
  m.rowHeights.splice(idx, 1);
  return m;
};

const removeCol = (model, idx) => {
  if ((model.grid[0]?.length || 0) <= 1) return model;
  const m = cloneModel(model);
  m.grid.forEach((row) => row.splice(idx, 1));
  m.colWidths.splice(idx, 1);
  const total = m.colWidths.reduce((a, b) => a + b, 0);
  if (total !== 100) m.colWidths[0] += 100 - total;
  return m;
};

const duplicateRow = (model, idx) => {
  const m = cloneModel(model);
  const newRow = m.grid[idx].map((c) => ({ ...c, id: uid(), component: c.component ? { ...c.component } : null }));
  m.grid.splice(idx + 1, 0, newRow);
  m.rowHeights.splice(idx + 1, 0, m.rowHeights[idx]);
  return m;
};

const duplicateCol = (model, idx) => {
  const m = cloneModel(model);
  m.grid.forEach((row, ri) => {
    const c = row[idx];
    row.splice(idx + 1, 0, { ...c, id: uid(), component: c.component ? { ...c.component } : null });
  });
  m.colWidths.splice(idx + 1, 0, m.colWidths[idx]);
  const total = m.colWidths.reduce((a, b) => a + b, 0);
  if (total !== 100) m.colWidths[0] += 100 - total;
  return m;
};

const mergeCells = (model, sel) => {
  if (!sel || sel.length < 2) return model;
  const m = cloneModel(model);
  const rows = sel.map((s) => s.r);
  const cols = sel.map((s) => s.c);
  const r1 = Math.min(...rows), r2 = Math.max(...rows);
  const c1 = Math.min(...cols), c2 = Math.max(...cols);
  const anchor = m.grid[r1][c1];
  anchor.rowspan = r2 - r1 + 1;
  anchor.colspan = c2 - c1 + 1;
  for (let r = r1; r <= r2; r++) {
    for (let c = c1; c <= c2; c++) {
      if (r === r1 && c === c1) continue;
      m.grid[r][c] = { ...createCell(), _merged: true, _anchorId: anchor.id };
    }
  }
  return m;
};

const splitCell = (model, row, col) => {
  const m = cloneModel(model);
  const cell = m.grid[row][col];
  if (cell.rowspan <= 1 && cell.colspan <= 1) return m;
  const rs = cell.rowspan || 1;
  const cs = cell.colspan || 1;
  cell.rowspan = 1;
  cell.colspan = 1;
  for (let r = row; r < row + rs; r++) {
    for (let c = col; c < col + cs; c++) {
      if (r === row && c === col) continue;
      m.grid[r][c] = createCell({ bgColor: cell.bgColor });
    }
  }
  return m;
};

const updateCellInModel = (model, row, col, patch) => {
  const m = cloneModel(model);
  m.grid[row][col] = { ...m.grid[row][col], ...patch };
  return m;
};

/* ──────── HTML export (Outlook-compatible) ──────── */
const componentToHtml = (comp) => {
  if (!comp) return '';
  const { type, props = {} } = comp;
  if (type === 'text') {
    return `<p style="margin:0;font-family:Arial,sans-serif;font-size:14px;color:#333;">${props.text || 'Text'}</p>`;
  }
  if (type === 'button') {
    const bg = props.bg || '#7a6a55';
    const color = props.color || '#ffffff';
    const radius = props.radius != null ? props.radius : 10;
    return `<table border="0" cellpadding="0" cellspacing="0" role="presentation" style="margin:0 auto;"><tr><td align="center" style="background:${bg};border-radius:${radius}px;padding:12px 24px;"><a href="${props.url || '#'}" target="_blank" style="color:${color};font-family:Arial,sans-serif;font-size:14px;font-weight:bold;text-decoration:none;display:inline-block;">${props.text || 'Button'}</a></td></tr></table>`;
  }
  if (type === 'divider') {
    const color = props.color || '#7a6a55';
    const thickness = props.thickness || 2;
    return `<table border="0" cellpadding="0" cellspacing="0" width="100%" role="presentation"><tr><td style="border-top:${thickness}px solid ${color};font-size:0;line-height:0;">&nbsp;</td></tr></table>`;
  }
  if (type === 'social') {
    return `<p style="margin:0;font-family:Arial,sans-serif;font-size:12px;color:#888;text-align:center;">Social Links</p>`;
  }
  if (type === 'image') {
    const src = props.src || '';
    if (!src) return `<p style="margin:0;color:#999;font-size:12px;text-align:center;">[Image placeholder]</p>`;
    return `<img src="${src}" alt="${props.alt || ''}" width="${props.width || 'auto'}" style="display:block;max-width:100%;height:auto;" />`;
  }
  return '';
};

const buildRowHtml = (model, ri, colWidths, bw, bc, bsty) => {
  const row = model.grid[ri];
  let html = '<tr>';
  row.forEach((cell, ci) => {
    if (cell._merged) return;
    const cs = cell.colspan > 1 ? ` colspan="${cell.colspan}"` : '';
    const rs = cell.rowspan > 1 ? ` rowspan="${cell.rowspan}"` : '';
    const colW = colWidths[ci] ? `width:${colWidths[ci]}%` : '';
    const cbs = cell.borderStyle != null ? cell.borderStyle : bsty;
    const cbw = cell.borderWidth != null ? cell.borderWidth : bw;
    const cbc = cell.borderColor != null ? cell.borderColor : bc;
    const titleAttr = cell.isTitle ? ' data-title="true"' : '';
    const fontWeight = cell.isTitle ? 'font-weight:bold;' : '';
    const styles = [
      cbs === 'none' ? 'border:none' : `border:${cbw}px ${cbs} ${cbc}`,
      `background-color:${cell.bgColor || '#ffffff'}`,
      `padding:${cell.padding || 8}px`,
      `text-align:${cell.align || 'left'}`,
      `vertical-align:${cell.valign || 'top'}`,
      fontWeight,
      colW,
      cell.height ? `height:${cell.height}px` : '',
    ].filter(Boolean).join(';');
    const inner = cell.component ? componentToHtml(cell.component) : (cell.content || '&nbsp;');
    html += `<td${cs}${rs}${titleAttr} style="${styles}">${inner}</td>`;
  });
  html += '</tr>';
  return html;
};

const tableModelToHtml = (model) => {
  const bw = model.tableBorderWidth != null ? model.tableBorderWidth : 1;
  const bc = model.tableBorderColor || '#d0d5dd';
  const bsty = model.tableBorderStyle || 'solid';
  const colWidths = model.colWidths || [];
  const loopConds = model.tableLoopConditions || [];

  const titleRows = [];
  const dataRows = [];
  model.grid.forEach((row, ri) => {
    const hasTitle = row.some((c) => c.isTitle && !c._merged);
    if (hasTitle) titleRows.push(ri);
    else dataRows.push(ri);
  });

  let html = `<table width="100%" border="0" cellpadding="0" cellspacing="0" role="presentation" style="border-collapse:collapse;width:100%;mso-table-lspace:0pt;mso-table-rspace:0pt;">`;

  if (loopConds.length > 0 && titleRows.length > 0) {
    titleRows.forEach((ri) => { html += buildRowHtml(model, ri, colWidths, bw, bc, bsty); });
    loopConds.forEach((cond) => {
      if (cond.type === 'if') html += `\n{{if .${cond.value}}}`;
      if (cond.type === 'range') html += `\n{{range .${cond.value}}}`;
    });
    dataRows.forEach((ri) => { html += buildRowHtml(model, ri, colWidths, bw, bc, bsty); });
    loopConds.forEach(() => { html += '\n{{end}}'; });
  } else {
    model.grid.forEach((row, ri) => { html += buildRowHtml(model, ri, colWidths, bw, bc, bsty); });
  }

  html += '</table>';
  return html;
};

/* ────────────────── Inline component renderers ─────────────────── */
const CellComponentRenderer = ({ comp, cellId }) => {
  if (!comp) return null;
  const { type, props = {} } = comp;

  if (type === 'text') {
    return (
      <div className="etbl-cell-comp etbl-comp-text" style={{ fontFamily: 'Arial, sans-serif', fontSize: 14, color: '#333' }}>
        {props.text || 'Text block'}
      </div>
    );
  }
  if (type === 'button') {
    return (
      <div className="etbl-cell-comp etbl-comp-button" style={{ textAlign: props.align || 'center' }}>
        <span style={{
          display: 'inline-block',
          padding: '10px 22px',
          background: props.bg || '#7a6a55',
          color: props.color || '#fff',
          borderRadius: (props.radius != null ? props.radius : 10),
          fontWeight: 600,
          fontSize: 14,
          fontFamily: 'Arial, sans-serif',
          cursor: 'default',
          lineHeight: '1.2',
        }}>{props.text || 'Button'}</span>
      </div>
    );
  }
  if (type === 'divider') {
    return (
      <div className="etbl-cell-comp etbl-comp-divider" style={{ padding: '6px 0' }}>
        <div style={{ borderTop: `${props.thickness || 2}px solid ${props.color || '#7a6a55'}` }} />
      </div>
    );
  }
  if (type === 'social') {
    return (
      <div className="etbl-cell-comp etbl-comp-social" style={{ textAlign: 'center', color: '#888', fontSize: 12, padding: '4px 0' }}>
        ◉ Social Links
      </div>
    );
  }
  if (type === 'image') {
    return (
      <div className="etbl-cell-comp etbl-comp-image" style={{ textAlign: 'center' }}>
        {props.src ? (
          <img src={props.src} alt={props.alt || ''} style={{ maxWidth: '100%', height: 'auto', display: 'block' }} />
        ) : (
          <div style={{ padding: 16, background: '#f8f9fa', border: '1px dashed #ccc', borderRadius: 4, color: '#999', fontSize: 12 }}>
            Image placeholder
          </div>
        )}
      </div>
    );
  }
  return <div style={{ color: '#ccc', fontSize: 11 }}>Unknown component</div>;
};

/* ─────────────────── Grid Size Selector ────────────────────────── */
const TableGridSelector = ({ onSelect, onCancel }) => {
  const [hR, setHR] = useState(-1);
  const [hC, setHC] = useState(-1);
  const MAX = 4;

  return (
    <div className="etbl-grid-selector">
      <div className="etbl-grid-selector-label">
        {hR >= 0 && hC >= 0 ? `${hR + 1} × ${hC + 1}` : 'Select table size'}
      </div>
      <div
        className="etbl-grid-selector-grid"
        onMouseLeave={() => { setHR(-1); setHC(-1); }}
      >
        {Array.from({ length: MAX }, (_, r) =>
          Array.from({ length: MAX }, (_, c) => (
            <div
              key={`${r}-${c}`}
              className={`etbl-grid-cell ${r <= hR && c <= hC ? 'active' : ''}`}
              onMouseEnter={() => { setHR(r); setHC(c); }}
              onClick={() => onSelect(r + 1, c + 1)}
            />
          )),
        )}
      </div>
    </div>
  );
};

/* ────────────────────── TABLE EDITOR (Canvas) ──────────────────── */
const EmailTableEditor = ({
  model,
  onChange,
  onSelectCell,
  selectedCellId,
  onRemoveTable,
  readOnly = false,
}) => {
  const tableRef = useRef(null);
  const [dragOverCellId, setDragOverCellId] = useState(null);
  const [resizeInfo, setResizeInfo] = useState(null);
  const [multiSel, setMultiSel] = useState([]);

  // Column resize
  const startColResize = useCallback((e, colIdx) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidths = [...model.colWidths];
    const tblEl = tableRef.current;
    const tblW = tblEl ? tblEl.getBoundingClientRect().width : 600;

    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      const dxPct = (dx / tblW) * 100;
      const newWidths = [...startWidths];
      newWidths[colIdx] = clamp(startWidths[colIdx] + dxPct, 5, 90);
      if (colIdx + 1 < newWidths.length) {
        newWidths[colIdx + 1] = clamp(startWidths[colIdx + 1] - dxPct, 5, 90);
      }
      const m = cloneModel(model);
      m.colWidths = newWidths;
      onChange(m);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [model, onChange]);

  // Drop handler
  const handleDrop = useCallback((e, rowIdx, colIdx) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverCellId(null);
    if (readOnly) return;
    const compType = e.dataTransfer?.getData('component') || e.dataTransfer?.getData('text/plain');
    if (!compType) return;
    const defaultProps = {
      button: { text: 'Button', url: '', bg: '#7a6a55', color: '#ffffff', radius: 10, align: 'center' },
      divider: { color: '#7a6a55', thickness: 2 },
      social: { align: 'center' },
      text: { text: 'Text block' },
      image: { src: '', alt: '' },
    };
    const m = updateCellInModel(model, rowIdx, colIdx, {
      component: { type: compType, props: { ...(defaultProps[compType] || {}) } },
    });
    onChange(m);
    onSelectCell && onSelectCell(m.grid[rowIdx][colIdx].id);
  }, [model, onChange, onSelectCell, readOnly]);

  // Drag-over feedback
  const handleDragOver = useCallback((e, cellId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDragOverCellId(cellId);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverCellId(null);
  }, []);

  // Cell click
  const handleCellClick = useCallback((e, cellId) => {
    if (e.shiftKey && selectedCellId) {
      // multi-select for merge
      setMultiSel((prev) => {
        const exists = prev.find((s) => s.id === cellId);
        if (exists) return prev.filter((s) => s.id !== cellId);
        let r, c;
        model.grid.forEach((row, ri) => row.forEach((cell, ci) => {
          if (cell.id === cellId) { r = ri; c = ci; }
        }));
        return [...prev, { id: cellId, r, c }];
      });
    } else {
      setMultiSel([]);
      onSelectCell && onSelectCell(cellId);
    }
  }, [model, onSelectCell, selectedCellId]);

  const handleMerge = useCallback(() => {
    if (multiSel.length < 2) return;
    const next = mergeCells(model, multiSel);
    onChange(next);
    setMultiSel([]);
  }, [model, multiSel, onChange]);

  const colCount = model.grid[0]?.length || 0;

  return (
    <div className="etbl-editor-wrap" ref={tableRef}>
      {/* Toolbar floating above */}
      {!readOnly && (
        <div className="etbl-toolbar">
          <button type="button" className="etbl-tb-btn" title="Add row" onClick={() => onChange(addRow(model))}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            <span>Row</span>
          </button>
          <button type="button" className="etbl-tb-btn" title="Add column" onClick={() => onChange(addCol(model))}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            <span>Col</span>
          </button>
          <span className="etbl-tb-sep" />
          {multiSel.length >= 2 && (
            <button type="button" className="etbl-tb-btn etbl-tb-merge" title="Merge selected cells" onClick={handleMerge}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="12" y1="3" x2="12" y2="21"/></svg>
              <span>Merge</span>
            </button>
          )}
          <button type="button" className="etbl-tb-btn etbl-tb-del" title="Delete table" onClick={onRemoveTable}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/></svg>
          </button>
        </div>
      )}

      {/* The actual table */}
      <table className="etbl-table" style={{ width: `${model.tableWidth}%` }}>
        <tbody>
          {model.grid.map((row, ri) => (
            <tr key={ri} className="etbl-row">
              {row.map((cell, ci) => {
                if (cell._merged) return null;
                const isSelected = selectedCellId === cell.id;
                const isDragOver = dragOverCellId === cell.id;
                const isMulti = multiSel.some((s) => s.id === cell.id);
                const tbw = model.tableBorderWidth || 1;
                const tbc = model.tableBorderColor || '#d0d5dd';
                const tbst = model.tableBorderStyle || 'solid';
                const cbst = cell.borderStyle != null ? cell.borderStyle : tbst;
                const cbw = cell.borderWidth != null ? cell.borderWidth : tbw;
                const cbc = cell.borderColor != null ? cell.borderColor : tbc;

                return (
                  <td
                    key={cell.id}
                    className={[
                      'etbl-cell',
                      isSelected ? 'etbl-cell-selected' : '',
                      isDragOver ? 'etbl-cell-dragover' : '',
                      isMulti ? 'etbl-cell-multi' : '',
                      cell.component ? 'etbl-cell-filled' : 'etbl-cell-empty',
                    ].join(' ')}
                    colSpan={cell.colspan > 1 ? cell.colspan : undefined}
                    rowSpan={cell.rowspan > 1 ? cell.rowspan : undefined}
                    style={{
                      border: cbst === 'none' ? 'none' : `${cbw}px ${cbst} ${cbc}`,
                      backgroundColor: cell.bgColor || '#fff',
                      padding: cell.padding != null ? cell.padding : 8,
                      textAlign: cell.align || 'left',
                      verticalAlign: cell.valign || 'top',
                      width: model.colWidths[ci] ? `${model.colWidths[ci]}%` : 'auto',
                      minHeight: 40,
                      position: 'relative',
                      transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
                    }}
                    onClick={(e) => handleCellClick(e, cell.id)}
                    onDragOver={(e) => handleDragOver(e, cell.id)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, ri, ci)}
                  >
                    {cell.component ? (
                      <CellComponentRenderer comp={cell.component} cellId={cell.id} />
                    ) : (
                      <div className="etbl-cell-placeholder">
                        <span>Drop here</span>
                      </div>
                    )}

                    {/* Col resize handle */}
                    {ci < colCount - 1 && !readOnly && (
                      <div
                        className="etbl-col-resize"
                        onMouseDown={(e) => startColResize(e, ci)}
                      />
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* ──────────── Predefined Table Styles ──────────── */
const TABLE_STYLES = [
  {
    id: 'plain',
    name: 'Plain',
    preview: { bg: '#ffffff', headerBg: '#ffffff', borderColor: '#000000', borderWidth: 1, borderStyle: 'solid', headerText: '#000', bodyText: '#333' },
  },
  {
    id: 'grid-light',
    name: 'Grid Light',
    preview: { bg: '#ffffff', headerBg: '#f3f4f6', borderColor: '#d1d5db', borderWidth: 1, borderStyle: 'solid', headerText: '#111', bodyText: '#333' },
  },
  {
    id: 'blue-header',
    name: 'Blue Header',
    preview: { bg: '#ffffff', headerBg: '#2563eb', borderColor: '#2563eb', borderWidth: 1, borderStyle: 'solid', headerText: '#ffffff', bodyText: '#333' },
  },
  {
    id: 'green-header',
    name: 'Green Header',
    preview: { bg: '#ffffff', headerBg: '#16a34a', borderColor: '#16a34a', borderWidth: 1, borderStyle: 'solid', headerText: '#ffffff', bodyText: '#333' },
  },
  {
    id: 'dark',
    name: 'Dark',
    preview: { bg: '#1f2937', headerBg: '#111827', borderColor: '#374151', borderWidth: 1, borderStyle: 'solid', headerText: '#f9fafb', bodyText: '#d1d5db' },
  },
  {
    id: 'orange-accent',
    name: 'Orange Accent',
    preview: { bg: '#fff7ed', headerBg: '#ea580c', borderColor: '#ea580c', borderWidth: 1, borderStyle: 'solid', headerText: '#ffffff', bodyText: '#333' },
  },
  {
    id: 'purple-accent',
    name: 'Purple Accent',
    preview: { bg: '#faf5ff', headerBg: '#7c3aed', borderColor: '#7c3aed', borderWidth: 1, borderStyle: 'solid', headerText: '#ffffff', bodyText: '#333' },
  },
  {
    id: 'minimal',
    name: 'Minimal',
    preview: { bg: '#ffffff', headerBg: '#ffffff', borderColor: '#e5e7eb', borderWidth: 1, borderStyle: 'solid', headerText: '#6b7280', bodyText: '#9ca3af' },
  },
  {
    id: 'no-border',
    name: 'No Border',
    preview: { bg: '#ffffff', headerBg: '#f9fafb', borderColor: '#ffffff', borderWidth: 0, borderStyle: 'none', headerText: '#111', bodyText: '#333' },
  },
];

const applyTableStyle = (model, style) => {
  const m = cloneModel(model);
  const s = style.preview;
  m.tableBorderColor = s.borderColor;
  m.tableBorderWidth = s.borderWidth;
  m.tableBorderStyle = s.borderStyle;
  m.grid.forEach((row, ri) => {
    row.forEach((cell) => {
      if (cell._merged) return;
      cell.bgColor = ri === 0 ? s.headerBg : s.bg;
    });
  });
  return m;
};

const applyEffect = (model, effect, enabled) => {
  const m = cloneModel(model);
  switch (effect) {
    case 'headerRow': {
      const hdrBg = enabled ? '#f3f4f6' : '#ffffff';
      if (m.grid[0]) m.grid[0].forEach((c) => { if (!c._merged) c.bgColor = hdrBg; });
      break;
    }
    case 'bandedRows': {
      m.grid.forEach((row, ri) => {
        if (ri === 0) return;
        const bg = enabled ? (ri % 2 === 0 ? '#f9fafb' : '#ffffff') : '#ffffff';
        row.forEach((c) => { if (!c._merged) c.bgColor = bg; });
      });
      break;
    }
    case 'bandedCols': {
      m.grid.forEach((row) => {
        row.forEach((c, ci) => {
          if (c._merged) return;
          const bg = enabled ? (ci % 2 === 0 ? '#f0f0f0' : '#ffffff') : '#ffffff';
          c.bgColor = bg;
        });
      });
      break;
    }
    case 'firstCol': {
      const bg = enabled ? '#e5e7eb' : '#ffffff';
      m.grid.forEach((row) => { if (row[0] && !row[0]._merged) row[0].bgColor = bg; });
      break;
    }
    case 'lastCol': {
      const bg = enabled ? '#e5e7eb' : '#ffffff';
      m.grid.forEach((row) => {
        const last = row[row.length - 1];
        if (last && !last._merged) last.bgColor = bg;
      });
      break;
    }
    default: break;
  }
  return m;
};

/* ────────────────────── RIGHT PANEL (Table props) ──────────────── */
const BORDER_STYLE_OPTIONS = [
  { val: 'solid', label: 'Solid', svg: <line x1="2" y1="12" x2="22" y2="12" strokeWidth="2" /> },
  { val: 'dashed', label: 'Dashed', svg: <line x1="2" y1="12" x2="22" y2="12" strokeWidth="2" strokeDasharray="6 3" /> },
  { val: 'dotted', label: 'Dotted', svg: <line x1="2" y1="12" x2="22" y2="12" strokeWidth="2" strokeDasharray="2 3" /> },
  { val: 'none', label: 'None', svg: <><line x1="2" y1="12" x2="22" y2="12" strokeWidth="1" opacity="0.3" /><line x1="4" y1="8" x2="20" y2="16" strokeWidth="1.5" stroke="#e53e3e" /></> },
];

const COLOR_SWATCHES = ['#ffffff', '#f3f4f6', '#fef3c7', '#dcfce7', '#dbeafe', '#fce7f3', '#ede9fe', '#1f2937'];

const BorderStylePicker = ({ active, onChange: onPick }) => (
  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
    {BORDER_STYLE_OPTIONS.map((s) => (
      <button
        key={s.val}
        type="button"
        title={s.label}
        onClick={() => onPick(s.val)}
        style={{
          flex: '1 1 0', minWidth: 52, padding: '5px 3px',
          border: active === s.val ? '2px solid #29e67c' : '1px solid #d1d5db',
          borderRadius: 6, background: active === s.val ? '#f0fdf4' : '#fff',
          cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
        }}
      >
        <svg width="22" height="18" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2">{s.svg}</svg>
        <span style={{ fontSize: 9, color: '#555' }}>{s.label}</span>
      </button>
    ))}
  </div>
);

const ColorSwatchRow = ({ onPick }) => (
  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 4 }}>
    {COLOR_SWATCHES.map((c) => (
      <div
        key={c}
        onClick={() => onPick(c)}
        style={{
          width: 26, height: 26, borderRadius: 4, background: c,
          border: '1px solid #d1d5db', cursor: 'pointer',
          boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.05)',
        }}
      />
    ))}
    <div style={{ position: 'relative', width: 26, height: 26 }}>
      <input
        type="color"
        onChange={(e) => onPick(e.target.value)}
        style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%' }}
      />
      <div style={{
        width: 26, height: 26, borderRadius: 4, border: '1px solid #d1d5db',
        background: 'conic-gradient(red, yellow, lime, aqua, blue, magenta, red)',
        pointerEvents: 'none',
      }} />
    </div>
  </div>
);

const TableRightPanel = ({
  model,
  onChange,
  selectedCellId,
  onClose,
  onRemoveTable,
}) => {
  const [cellSel, setCellSel] = useState([]); // [{ri, ci}]
  const dragAnchor = useRef(null);  // {ri, ci} — starting cell of drag
  const isDragging = useRef(false);

  // Find selected cell info (from canvas click)
  let selRow = -1, selCol = -1, selCell = null;
  if (selectedCellId) {
    model.grid.forEach((row, ri) => row.forEach((cell, ci) => {
      if (cell.id === selectedCellId) { selRow = ri; selCol = ci; selCell = cell; }
    }));
  }

  const hasCanvasSel = selCell != null;
  const hasCellSel = cellSel.length > 0;
  const selCount = cellSel.length;

  // Helpers
  const isCellSelected = (ri, ci) => cellSel.some((s) => s.ri === ri && s.ci === ci);

  // Build rectangular selection from anchor to current cell
  const buildRectSel = (anchor, current) => {
    const r1 = Math.min(anchor.ri, current.ri), r2 = Math.max(anchor.ri, current.ri);
    const c1 = Math.min(anchor.ci, current.ci), c2 = Math.max(anchor.ci, current.ci);
    const sel = [];
    for (let r = r1; r <= r2; r++) {
      for (let c = c1; c <= c2; c++) {
        if (model.grid[r] && model.grid[r][c] && !model.grid[r][c]._merged) {
          sel.push({ ri: r, ci: c });
        }
      }
    }
    return sel;
  };

  // Drag-to-select handlers
  const handleCellMouseDown = (e, ri, ci) => {
    e.preventDefault();
    if (e.shiftKey || e.ctrlKey || e.metaKey) {
      // Toggle add/remove individual cell
      setCellSel((prev) => {
        const exists = prev.find((s) => s.ri === ri && s.ci === ci);
        if (exists) return prev.filter((s) => !(s.ri === ri && s.ci === ci));
        return [...prev, { ri, ci }];
      });
      return;
    }
    isDragging.current = true;
    dragAnchor.current = { ri, ci };
    setCellSel([{ ri, ci }]);
    const onUp = () => {
      isDragging.current = false;
      dragAnchor.current = null;
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mouseup', onUp);
  };

  const handleCellMouseEnter = (ri, ci) => {
    if (!isDragging.current || !dragAnchor.current) return;
    setCellSel(buildRectSel(dragAnchor.current, { ri, ci }));
  };

  const selectAllCells = () => {
    const all = [];
    model.grid.forEach((row, ri) => row.forEach((cell, ci) => {
      if (!cell._merged) all.push({ ri, ci });
    }));
    setCellSel(all);
  };

  const clearCellSel = () => setCellSel([]);

  // ── Merge / Split helpers ──
  const isValidMergeSelection = () => {
    if (cellSel.length < 2) return false;
    const rows = cellSel.map((s) => s.ri);
    const cols = cellSel.map((s) => s.ci);
    const r1 = Math.min(...rows), r2 = Math.max(...rows);
    const c1 = Math.min(...cols), c2 = Math.max(...cols);
    let expected = 0;
    for (let r = r1; r <= r2; r++) {
      for (let c = c1; c <= c2; c++) {
        const cell = model.grid[r]?.[c];
        if (cell && !cell._merged) expected++;
      }
    }
    return cellSel.length === expected;
  };

  const canMerge = isValidMergeSelection();

  const canSplit = cellSel.length === 1 && (() => {
    const { ri, ci } = cellSel[0];
    const c = model.grid[ri]?.[ci];
    return c && (c.colspan > 1 || c.rowspan > 1);
  })();

  const handleMergeCells = () => {
    if (!canMerge) return;
    const mapped = cellSel.map((s) => ({ r: s.ri, c: s.ci }));
    const next = mergeCells(model, mapped);
    onChange(next);
    setCellSel([]);
  };

  const handleSplitCell = () => {
    if (!canSplit || cellSel.length !== 1) return;
    const { ri, ci } = cellSel[0];
    const next = splitCell(model, ri, ci);
    onChange(next);
    setCellSel([]);
  };

  const updateTable = (patch) => {
    const m = cloneModel(model);
    Object.assign(m, patch);
    onChange(m);
  };

  // Apply to selected cells (from mini-grid) or fallback to table-level
  const applyToSelectedCells = (patch) => {
    const m = cloneModel(model);
    for (const { ri, ci } of cellSel) {
      if (m.grid[ri] && m.grid[ri][ci] && !m.grid[ri][ci]._merged) {
        Object.assign(m.grid[ri][ci], patch);
      }
    }
    onChange(m);
  };

  const clearSelectedCellOverrides = () => {
    applyToSelectedCells({ borderStyle: null, borderWidth: null, borderColor: null });
  };

  const handleToggleTitle = () => {
    const m = cloneModel(model);
    const allTitle = cellSel.every(({ ri, ci }) => m.grid[ri]?.[ci]?.isTitle);
    for (const { ri, ci } of cellSel) {
      if (m.grid[ri] && m.grid[ri][ci] && !m.grid[ri][ci]._merged) {
        m.grid[ri][ci].isTitle = !allTitle;
      }
    }
    onChange(m);
  };

  const allSelTitle = hasCellSel && cellSel.every(({ ri, ci }) => model.grid[ri]?.[ci]?.isTitle);

  // Apply bg color: selected cells > selected row > entire table
  const applyBgColor = (color) => {
    const m = cloneModel(model);
    if (hasCellSel) {
      for (const { ri, ci } of cellSel) {
        if (m.grid[ri] && m.grid[ri][ci] && !m.grid[ri][ci]._merged) m.grid[ri][ci].bgColor = color;
      }
    } else if (hasCanvasSel) {
      m.grid[selRow].forEach((c) => { if (!c._merged) c.bgColor = color; });
    } else {
      m.grid.forEach((row) => row.forEach((c) => { if (!c._merged) c.bgColor = color; }));
    }
    onChange(m);
  };

  // Resolve what the "active" border style is for the cell selection
  const getSelBorderInfo = () => {
    if (!hasCellSel) return { style: null, width: null, color: null };
    const first = model.grid[cellSel[0].ri]?.[cellSel[0].ci];
    if (!first) return { style: null, width: null, color: null };
    return {
      style: first.borderStyle,
      width: first.borderWidth,
      color: first.borderColor,
    };
  };
  const selBorder = getSelBorderInfo();

  const tableBorderStyle = model.tableBorderStyle || 'solid';

  return (
    <>
      <div className="email-editor-header">
        <h5 className="email-edit-text mt-4">Edit Table</h5>
        <button type="button" className="etbl-panel-close" onClick={onClose} aria-label="Close">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      <div className="email-editor-panel etbl-right-scroll" style={{ overflowY: 'auto', overflowX: 'hidden', maxHeight: 530, padding: '12px 10px 16px 16px', margin: '0 0 0 1.5rem', boxSizing: 'border-box' }}>

        {/* ══════ Cell Selector Mini-Grid ══════ */}
        <div className="etbl-props-section">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <div className="etbl-struct-label" style={{ margin: 0 }}>Select Cells</div>
            <div style={{ display: 'flex', gap: 4 }}>
              <button type="button" onClick={selectAllCells}
                style={{ fontSize: 10, padding: '2px 6px', border: '1px solid #d1d5db', borderRadius: 4, background: '#f9fafb', cursor: 'pointer', color: '#555' }}>
                All
              </button>
              {hasCellSel && (
                <button type="button" onClick={clearCellSel}
                  style={{ fontSize: 10, padding: '2px 6px', border: '1px solid #d1d5db', borderRadius: 4, background: '#fef2f2', cursor: 'pointer', color: '#dc2626' }}>
                  Clear
                </button>
              )}
            </div>
          </div>
          <div style={{ fontSize: 10, color: '#888', marginBottom: 4 }}>Click &amp; drag to select. Ctrl/Shift + click to add.</div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: (model.colWidths || []).map((w) => `${Math.max(5, w)}fr`).join(' '),
              gap: 2, border: '1px solid #e5e7eb', borderRadius: 6, padding: 4, background: '#fafafa',
              userSelect: 'none',
            }}
          >
            {model.grid.map((row, ri) =>
              row.map((cell, ci) => {
                if (cell._merged) return null;
                const sel = isCellSelected(ri, ci);
                const isMerged = cell.colspan > 1 || cell.rowspan > 1;
                return (
                  <div
                    key={`${ri}-${ci}`}
                    onMouseDown={(e) => handleCellMouseDown(e, ri, ci)}
                    onMouseEnter={() => handleCellMouseEnter(ri, ci)}
                    style={{
                      gridColumn: `${ci + 1} / span ${cell.colspan || 1}`,
                      gridRow: `${ri + 1} / span ${cell.rowspan || 1}`,
                      position: 'relative',
                      height: 28, borderRadius: 3, cursor: 'cell',
                      background: sel ? '#bbf7d0' : (cell.bgColor !== '#ffffff' ? cell.bgColor : '#fff'),
                      border: sel ? '2px solid #16a34a' : '1px solid #d1d5db',
                      transition: 'background 0.1s, border 0.1s',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                    title={`Row ${ri + 1}, Col ${ci + 1}${isMerged ? ' (merged)' : ''}`}
                  >
                    {cell.isTitle && (
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="#f59e0b" stroke="#f59e0b" strokeWidth="1" style={{ position: 'absolute', top: 1, right: 1 }}>
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26" />
                      </svg>
                    )}
                    {isMerged && (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" style={{ opacity: 0.6 }}>
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <line x1="12" y1="3" x2="12" y2="21" strokeDasharray="3 2" />
                        <line x1="3" y1="12" x2="21" y2="12" strokeDasharray="3 2" />
                      </svg>
                    )}
                  </div>
                );
              })
            )}
          </div>
          {hasCellSel && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
              <div style={{ fontSize: 11, color: '#16a34a', fontWeight: 600 }}>
                {selCount} cell{selCount > 1 ? 's' : ''} selected
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                <button type="button" onClick={handleToggleTitle}
                  title={allSelTitle ? 'Remove title marking' : 'Mark as table header/title'}
                  className="etbl-merge-split-btn"
                  style={{ background: allSelTitle ? '#fef3c7' : undefined, borderColor: allSelTitle ? '#f59e0b' : undefined }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill={allSelTitle ? '#f59e0b' : 'none'} stroke={allSelTitle ? '#f59e0b' : 'currentColor'} strokeWidth="2">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26" />
                  </svg>
                  Title
                </button>
                {canMerge && (
                  <button type="button" onClick={handleMergeCells} title="Merge selected cells"
                    className="etbl-merge-split-btn etbl-merge-btn">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <line x1="12" y1="3" x2="12" y2="21" />
                    </svg>
                    Merge
                  </button>
                )}
                {canSplit && (
                  <button type="button" onClick={handleSplitCell} title="Split merged cell"
                    className="etbl-merge-split-btn etbl-split-btn">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="8" height="18" rx="1" />
                      <rect x="13" y="3" width="8" height="18" rx="1" />
                    </svg>
                    Split
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <hr className="horizontal-lines" />

        {/* ══════ Per-Cell Styling (when cells selected) ══════ */}
        {hasCellSel && (
          <>
            <div className="etbl-props-section">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <div className="etbl-struct-label" style={{ margin: 0 }}>Cell Border</div>
                {selBorder.style != null && (
                  <button type="button" onClick={clearSelectedCellOverrides}
                    style={{ fontSize: 10, padding: '2px 6px', border: '1px solid #d1d5db', borderRadius: 4, background: '#fef2f2', cursor: 'pointer', color: '#dc2626' }}>
                    Reset to table
                  </button>
                )}
              </div>

              {/* Cell border style */}
              <BorderStylePicker
                active={selBorder.style || tableBorderStyle}
                onChange={(val) => applyToSelectedCells({ borderStyle: val, borderWidth: val === 'none' ? 0 : Math.max(1, selBorder.width || model.tableBorderWidth || 1) })}
              />

              {/* Cell border width */}
              {(selBorder.style || tableBorderStyle) !== 'none' && (
                <div className="etbl-prop-row" style={{ marginTop: 6 }}>
                  <label className="etbl-prop-label">Width</label>
                  <input
                    type="range" min={1} max={4} step={1}
                    value={selBorder.width || model.tableBorderWidth || 1}
                    onChange={(e) => applyToSelectedCells({ borderWidth: Number(e.target.value) })}
                    className="etbl-range"
                  />
                </div>
              )}

              {/* Cell border color */}
              {(selBorder.style || tableBorderStyle) !== 'none' && (
                <div className="etbl-prop-row" style={{ marginTop: 4 }}>
                  <label className="etbl-prop-label">Color</label>
                  <input
                    type="color"
                    value={selBorder.color || model.tableBorderColor || '#000000'}
                    onChange={(e) => applyToSelectedCells({ borderColor: e.target.value })}
                    className="etbl-color-input"
                  />
                </div>
              )}
            </div>

            <hr className="horizontal-lines" />
          </>
        )}

        {/* ══════ Table-level Borders ══════ */}
        <div className="etbl-props-section">
          <div className="etbl-struct-label">Table Border</div>
          <BorderStylePicker
            active={tableBorderStyle}
            onChange={(val) => updateTable({ tableBorderStyle: val, tableBorderWidth: val === 'none' ? 0 : Math.max(1, model.tableBorderWidth) })}
          />
          {tableBorderStyle !== 'none' && (
            <div className="etbl-prop-row" style={{ marginTop: 6 }}>
              <label className="etbl-prop-label">Width</label>
              <input
                type="range" min={1} max={4} step={1}
                value={model.tableBorderWidth || 1}
                onChange={(e) => updateTable({ tableBorderWidth: Number(e.target.value) })}
                className="etbl-range"
              />
            </div>
          )}
          {tableBorderStyle !== 'none' && (
            <div className="etbl-prop-row" style={{ marginTop: 4 }}>
              <label className="etbl-prop-label">Color</label>
              <input
                type="color"
                value={model.tableBorderColor || '#000000'}
                onChange={(e) => updateTable({ tableBorderColor: e.target.value })}
                className="etbl-color-input"
              />
            </div>
          )}
        </div>

        <hr className="horizontal-lines" />

        {/* ══════ Background Color ══════ */}
        <div className="etbl-props-section">
          <div className="etbl-struct-label">
            Background Color {hasCellSel ? `(${selCount} cell${selCount > 1 ? 's' : ''})` : hasCanvasSel ? `(Row ${selRow + 1})` : '(Entire Table)'}
          </div>
          <ColorSwatchRow onPick={applyBgColor} />
        </div>

        <hr className="horizontal-lines" />

        {/* ══════ Structure ══════ */}
        <div className="etbl-props-section">
          <div className="etbl-struct-label">Structure</div>
          <div className="etbl-struct-info">
            {model.grid.length} rows × {model.grid[0]?.length || 0} cols
          </div>
          <div className="etbl-struct-btns">
            <button type="button" className="etbl-struct-btn" onClick={() => onChange(addRow(model))}>+ Row</button>
            <button type="button" className="etbl-struct-btn" onClick={() => onChange(addCol(model))}>+ Col</button>
          </div>
        </div>

        <hr className="horizontal-lines" />

        {/* ══════ Effects ══════ */}
        <div className="etbl-props-section">
          <div className="etbl-struct-label">Effects</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
            {[
              { key: 'headerRow', label: 'Header Row', desc: 'Highlight the first row' },
              { key: 'bandedRows', label: 'Banded Rows', desc: 'Alternate row shading' },
              { key: 'bandedCols', label: 'Banded Columns', desc: 'Alternate column shading' },
              { key: 'firstCol', label: 'First Column', desc: 'Highlight first column' },
              { key: 'lastCol', label: 'Last Column', desc: 'Highlight last column' },
            ].map((eff) => (
              <label
                key={eff.key}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                  borderRadius: 6, border: '1px solid #e5e7eb', cursor: 'pointer',
                  fontSize: 12, color: '#333', background: '#fafafa',
                }}
              >
                <input
                  type="checkbox"
                  style={{ accentColor: '#29e67c' }}
                  onChange={(e) => onChange(applyEffect(model, eff.key, e.target.checked))}
                />
                <div>
                  <div style={{ fontWeight: 600 }}>{eff.label}</div>
                  <div style={{ fontSize: 10, color: '#888' }}>{eff.desc}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <hr className="horizontal-lines" />

        {/* ══════ Loop Conditions (for data rows) ══════ */}
        <div className="etbl-props-section">
          <div className="etbl-struct-label">Loop Conditions</div>
          <div style={{ fontSize: 10, color: '#888', marginBottom: 6 }}>Wrap data rows (non-title) with template conditions.</div>
          <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
            <button type="button"
              style={{ flex: 1, fontSize: 11, padding: '4px 8px', border: '1px solid #6c63ff', borderRadius: 4, background: '#f5f3ff', cursor: 'pointer', color: '#6c63ff', fontWeight: 600 }}
              onClick={() => {
                const m = cloneModel(model);
                if (!m.tableLoopConditions) m.tableLoopConditions = [];
                m.tableLoopConditions.push({ id: uid(), type: 'if', value: '' });
                onChange(m);
              }}>+ If</button>
            <button type="button"
              style={{ flex: 1, fontSize: 11, padding: '4px 8px', border: '1px solid #6c63ff', borderRadius: 4, background: '#f5f3ff', cursor: 'pointer', color: '#6c63ff', fontWeight: 600 }}
              onClick={() => {
                const m = cloneModel(model);
                if (!m.tableLoopConditions) m.tableLoopConditions = [];
                m.tableLoopConditions.push({ id: uid(), type: 'range', value: '' });
                onChange(m);
              }}>+ Range</button>
          </div>
          {(model.tableLoopConditions || []).map((cond, idx) => (
            <div key={cond.id || idx} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4, padding: '4px 6px', background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#6c63ff', whiteSpace: 'nowrap' }}>
                {`{{${cond.type} .`}
              </span>
              <input
                type="text"
                value={cond.value || ''}
                placeholder="variable"
                onChange={(e) => {
                  const m = cloneModel(model);
                  m.tableLoopConditions[idx].value = e.target.value;
                  onChange(m);
                }}
                style={{ flex: 1, fontSize: 11, padding: '2px 4px', border: '1px solid #d1d5db', borderRadius: 3, outline: 'none', minWidth: 0 }}
              />
              <span style={{ fontSize: 11, fontWeight: 600, color: '#6c63ff' }}>{'}}'}</span>
              <button type="button"
                onClick={() => {
                  const m = cloneModel(model);
                  m.tableLoopConditions.splice(idx, 1);
                  onChange(m);
                }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 14, padding: '0 2px', lineHeight: 1 }}>×</button>
            </div>
          ))}
          {(model.tableLoopConditions || []).length > 0 && (
            <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
              {(model.tableLoopConditions || []).length} condition{(model.tableLoopConditions || []).length > 1 ? 's' : ''} → {(model.tableLoopConditions || []).length} {'{{end}}'} auto-generated
            </div>
          )}
        </div>

        <hr className="horizontal-lines" />

        {/* ══════ Table Styles ══════ */}
        <div className="etbl-props-section">
          <div className="etbl-struct-label">Table Styles</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginTop: 6 }}>
            {TABLE_STYLES.map((ts) => (
              <div
                key={ts.id}
                onClick={() => { onChange(applyTableStyle(model, ts)); setCellSel([]); }}
                style={{
                  cursor: 'pointer', borderRadius: 6, border: '1px solid #d1d5db',
                  overflow: 'hidden', fontSize: 0, transition: 'box-shadow 0.15s',
                }}
                title={ts.name}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 0 0 2px #29e67c'; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{ height: 16, background: ts.preview.headerBg, borderBottom: `1px solid ${ts.preview.borderColor}` }} />
                  <div style={{ height: 12, background: ts.preview.bg }} />
                  <div style={{ height: 12, background: ts.id === 'no-border' ? ts.preview.bg : '#f9f9f9', borderTop: `1px solid ${ts.preview.borderColor}` }} />
                </div>
                <div style={{ fontSize: 10, textAlign: 'center', padding: '3px 2px', color: '#555', background: '#fafafa' }}>{ts.name}</div>
              </div>
            ))}
          </div>
        </div>

        <hr className="horizontal-lines" />

        {/* ══════ Cell Operations (when cells selected in mini-grid) ══════ */}
        {hasCellSel && (
          <div className="etbl-props-section">
            <div className="etbl-struct-label">Cell Operations</div>
            <div className="etbl-cell-ops">
              {/* Merge / Split */}
              {canMerge && (
                <button type="button" className="etbl-cell-op-btn" onClick={handleMergeCells}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 4, verticalAlign: 'middle' }}>
                    <rect x="3" y="3" width="18" height="18" rx="2" /><line x1="12" y1="3" x2="12" y2="21" />
                  </svg>
                  Merge {selCount} Cells
                </button>
              )}
              {canSplit && (
                <button type="button" className="etbl-cell-op-btn" onClick={handleSplitCell}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 4, verticalAlign: 'middle' }}>
                    <rect x="3" y="3" width="8" height="18" rx="1" /><rect x="13" y="3" width="8" height="18" rx="1" />
                  </svg>
                  Split Cell
                </button>
              )}
              {/* Row/Col ops (single cell) */}
              {selCount === 1 && (() => {
                const { ri, ci } = cellSel[0];
                return (
                  <>
                    <button type="button" className="etbl-cell-op-btn" onClick={() => onChange(addRow(model, ri))}>
                      Insert Row Below
                    </button>
                    <button type="button" className="etbl-cell-op-btn" onClick={() => onChange(addCol(model, ci))}>
                      Insert Col Right
                    </button>
                    {model.grid.length > 1 && (
                      <button type="button" className="etbl-cell-op-btn etbl-op-danger" onClick={() => { onChange(removeRow(model, ri)); setCellSel([]); }}>
                        Delete Row
                      </button>
                    )}
                    {(model.grid[0]?.length || 0) > 1 && (
                      <button type="button" className="etbl-cell-op-btn etbl-op-danger" onClick={() => { onChange(removeCol(model, ci)); setCellSel([]); }}>
                        Delete Col
                      </button>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {hasCellSel && <hr className="horizontal-lines" />}

        <button type="button" className="etbl-delete-btn" onClick={onRemoveTable}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>
          Delete Table
        </button>
      </div>
    </>
  );
};

/* ──────────────── MAIN EXPORT: hook + components ───────────────── */
export const useEmailTables = () => {
  const [tables, setTables] = useState({});           // { [tableId]: model }
  const [selectedTableId, setSelectedTableId] = useState(null);
  const [selectedCellId, setSelectedCellId] = useState(null);
  const tablesRef = useRef(tables);
  tablesRef.current = tables;

  const insertTable = useCallback((rows = 2, cols = 2) => {
    const m = createTableModel(rows, cols);
    setTables((prev) => ({ ...prev, [m.id]: m }));
    setSelectedTableId(m.id);
    return m;
  }, []);

  const updateTable = useCallback((id, newModel) => {
    setTables((prev) => ({ ...prev, [id]: newModel }));
  }, []);

  const removeTable = useCallback((id) => {
    setTables((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    if (selectedTableId === id) {
      setSelectedTableId(null);
      setSelectedCellId(null);
    }
  }, [selectedTableId]);

  const selectTable = useCallback((id) => {
    setSelectedTableId(id);
    setSelectedCellId(null);
  }, []);

  const getTableHtml = useCallback((id) => {
    const m = tablesRef.current[id];
    return m ? tableModelToHtml(m) : '';
  }, []);

  const getAllTablesHtml = useCallback(() => {
    const result = {};
    Object.entries(tablesRef.current).forEach(([id, m]) => {
      result[id] = tableModelToHtml(m);
    });
    return result;
  }, []);

  return {
    tables,
    selectedTableId,
    selectedCellId,
    setSelectedCellId,
    insertTable,
    updateTable,
    removeTable,
    selectTable,
    getTableHtml,
    getAllTablesHtml,
  };
};

/* Quill blot for embedding a table in the editor */
export const registerTableBlot = (Quill) => {
  const BlockEmbed = Quill.import('blots/block/embed');

  class EmailTableBlot extends BlockEmbed {
    static blotName = 'emailTable';
    static tagName = 'DIV';
    static className = 'q-embed-table';

    static create(value) {
      const node = super.create();
      node.setAttribute('contenteditable', 'false');
      node.dataset.comp = 'table';
      node.dataset.id = value?.id || '';
      node.dataset.tableId = value?.tableId || '';
      node.classList.add('q-comp', 'q-table');
      node.innerHTML = `<div class="q-table-inner">${value?.html || '<p style="color:#aaa;text-align:center;margin:12px 0">Table</p>'}</div>`;

      // delete btn
      const del = document.createElement('button');
      del.type = 'button';
      del.className = 'q-del';
      del.setAttribute('title', 'Delete');
      del.setAttribute('aria-label', 'Delete table');
      del.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M9 3h6a1 1 0 011 1v1h4v2h-1l-1 13a2 2 0 01-2 2H8a2 2 0 01-2-2L5 7H4V5h4V4a1 1 0 011-1zm2 3h2V5h-2v1zM8.999 9h2v9h-2V9zm4 0h2v9h-2V9z"/></svg>';
      node.appendChild(del);
      return node;
    }

    static value(node) {
      return {
        id: node.dataset.id,
        tableId: node.dataset.tableId,
        html: node.querySelector('.q-table-inner')?.innerHTML || '',
      };
    }
  }

  Quill.register(EmailTableBlot);
  return EmailTableBlot;
};

/* ──────────────────── Exported Components ──────────────────────── */
export { TableGridSelector, EmailTableEditor, TableRightPanel, tableModelToHtml, createTableModel, cloneModel };
